import { Drawer } from '@mui/material';
import { useState, useRef, useEffect } from 'react';
import * as Yup from 'yup';
import { Field, Formik, FormikProps } from 'formik';
import styles from './ManageRecipients.module.scss';
import { IconButton } from '../../button/IconButton/IconButton';
import { Icon } from '../../icon/Icon';
import { Autocomplete } from '../../input/Autocomplete/Autocomplete';
import { RECIPIENT_TYPES } from './constant';
import { Recipient, RecipientActionsType, SlackChannelPayload } from './interface';
import { FormControl } from '../../shared/components/FormControl/FormControl';
import { FormikTextField } from '../../input/FormikTextField/FormikTextField';
import { Button } from '../../button/Button';
import { NotificationsAPI } from '../NotificationsAPI';
import { Banner } from '../../banner/Banner';
import { getErrorMessageFromAxiosError } from '../../request/utils';

const validationSchema = Yup.object({
  email: Yup.string().email('Please enter a valid email address.').required('Email is required')
});

interface RecipientSettingsPaneProps {
  open: boolean;
  closeDrawer: () => void;
  resetRecipientList: () => void;
  switchRecipient: (value: Recipient) => void;
}

export function RecipientSettingsPane({
  switchRecipient,
  open,
  closeDrawer,
  resetRecipientList
}: RecipientSettingsPaneProps) {
  const type = RecipientActionsType.ADD;

  const [recipientType, setRecipientType] = useState(RECIPIENT_TYPES[0]);
  const [error, setError] = useState<string>('');
  const [slackAuthInProgress, setSlackAuthInProgress] = useState(false);
  const [slackChannelData, setSlackChannelData] = useState<SlackChannelPayload | null>();
  const [authWindow, setAuthWindow] = useState<Window | null>(null);

  const formikRef = useRef<FormikProps<{ email: string }> | null>(null);

  const onRecipientTypeChange = (value: { label: string; value: string }) => {
    setError('');
    setRecipientType(value);
  };

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'slackChannel' && event.newValue) {
        setSlackChannelData({ ...JSON.parse(event.newValue) });
      } else if (event.key === 'redirectErrorMessage' && event.newValue) {
        setError(event.newValue);
      }
      setSlackAuthInProgress(false);
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const onEmailRecipientActionClick = async () => {
    if (!formikRef.current?.isValid) {
      return;
    }

    await NotificationsAPI.addRecipient({
      type: 'email',
      channel_identifier: {
        email: formikRef.current?.values?.email
      }
    })
      .then(res => {
        switchRecipient(res.data);
        formikRef.current?.resetForm();
        resetRecipientList();
        closeDrawer();
      })
      .catch(_error => {
        formikRef.current?.setFieldError('email', getErrorMessageFromAxiosError(_error));
      });
  };

  const clearSlackChannelData = () => {
    setSlackAuthInProgress(false);
    localStorage.removeItem('slackChannel');
    localStorage.removeItem('redirectErrorMessage');
    setSlackChannelData(null);
  };

  const onSaveSlackRecipient = async () => {
    await NotificationsAPI.addRecipient(slackChannelData!)
      .then(res => {
        switchRecipient(res.data);
        resetRecipientList();
        clearSlackChannelData();
        closeDrawer();
      })
      .catch(err => {
        clearSlackChannelData();
        setError(getErrorMessageFromAxiosError(err));
      });
  };

  const handleSlackAuthentication = () => {
    setSlackAuthInProgress(true);
    setError('');
    if (authWindow && !authWindow.closed) {
      authWindow.close();
    }

    const redirectUri = `${window.location.origin}/slackAuthorization.html`;

    NotificationsAPI.getSlackAuthUrl(redirectUri)
      .then(res => {
        const authUrl = res.data.url;
        const newWindow = window.open(authUrl, 'authWindow');
        if (newWindow) {
          newWindow.focus();
          setAuthWindow(newWindow);
        } else {
          // eslint-disable-next-line no-alert
          window.alert('Popup window blocked! Please allow popups for this site.');
        }
      })
      .catch(err => {
        setError(getErrorMessageFromAxiosError(err));
      });
  };

  const onCloseDrawer = () => {
    clearSlackChannelData();
    setError('');
    formikRef.current?.resetForm();
    closeDrawer();
  };

  const actionDisplayName = 'Add New';

  const actionButtonText = 'Add Recipient';

  return (
    <Drawer
      open={open}
      onClose={onCloseDrawer}
      anchor='bottom'
      classes={{ paper: 'align-items-end' }}
      keepMounted
    >
      <div className={styles.RecipientSettingsDrawerContainer}>
        <IconButton
          dataId='close-drawer'
          label=''
          className={styles.closeDrawerBtn}
          onClick={() => onCloseDrawer()}
        >
          <Icon name='close' />
        </IconButton>

        <div className='text-subheading-2'>{actionDisplayName} Recipient</div>

        {type === RecipientActionsType.ADD ? (
          <div className='text-body-1 text-secondary'>
            Provide the details of the new recipient to add them to the Pipeline Subscriptions list
          </div>
        ) : null}

        <div className='mt-6 w-50'>
          <Autocomplete
            options={RECIPIENT_TYPES}
            disableClearable
            textFieldProps={{
              placeholder: 'Type',
              label: 'Type'
            }}
            value={recipientType}
            onChange={(_, value) =>
              onRecipientTypeChange(value as { label: string; value: string })
            }
            dataId='recipient-type'
          />
        </div>
        {recipientType.value === 'email' ? (
          <>
            <div className='mt-5'>
              <Formik
                initialValues={{ email: '' }}
                validationSchema={validationSchema}
                onSubmit={() => {}}
                innerRef={formikRef}
              >
                {() => (
                  <form noValidate>
                    <FormControl className='mb-0'>
                      <Field
                        dataId='recipient-add'
                        name='email'
                        label='Email'
                        component={FormikTextField}
                        autoFocus
                        required
                      />
                    </FormControl>
                  </form>
                )}
              </Formik>
            </div>

            <div className='mt-5'>
              <Button dataId='recipient-action' onClick={() => onEmailRecipientActionClick()}>
                {actionButtonText}
              </Button>
            </div>
          </>
        ) : null}

        {slackChannelData && recipientType?.value === 'slack' ? (
          <>
            <div className='d-flex mt-5 text-subheading-1 align-items-center'>
              <Icon name='slack' className='mr-1' size={3} />
              {slackChannelData.channel_identifier.channel_name}
            </div>
            <Button dataId='save-recipient' className='mt-5' onClick={onSaveSlackRecipient}>
              Save Recipient
            </Button>
          </>
        ) : null}

        {error ? (
          <Banner
            className='mt-5'
            open
            dataId='slack-auth-error'
            color='error'
            icon='error-outline'
          >
            {error}
          </Banner>
        ) : null}

        {recipientType.value === 'slack' && !slackChannelData ? (
          <Button
            dataId='auth-slack'
            icon='slack'
            className='mt-5'
            onClick={handleSlackAuthentication}
            showProgress={slackAuthInProgress}
            disabled={slackAuthInProgress}
          >
            Authenticate slack channel
          </Button>
        ) : null}
      </div>
    </Drawer>
  );
}
